/**
 * F-121 board-first canvas shell (docs/04 §5, amended 2026-07-10).
 *
 * A full-viewport canvas with a camera over board space. ONE transformed
 * container (`.camera-layer`) holds the dot background, the DOM table, and
 * the floating-elements layer:
 *
 *   transform: translate(-cam.x·zoom, -cam.y·zoom) scale(zoom)
 *
 * CSS transform drives zoom — children render in BOARD units and the browser
 * remaps DOM pointer coordinates under the transform natively (the reason
 * this was unsafe for the old canvas-grid renderer and is safe for the DOM
 * table). All screen↔board conversion still goes through the camera store /
 * @kola/shared board-space helpers, so overlays outside the transform (drop
 * targeting, ghosts) agree exactly with the transformed layers.
 *
 *  - PAN: space+drag, middle-button drag, wheel / two-finger scroll anywhere
 *    (the table never scrolls internally — F-10), left-drag on the background.
 *  - ZOOM: ctrl/cmd+wheel (incl. trackpad pinch) toward the pointer, 25–500%
 *    — trackpad/mouse only, no on-screen zoom control.
 *  - TOUCH (mobile controls differ from desktop on purpose): two fingers
 *    ALWAYS drive the camera — pinch zooms about the finger midpoint and the
 *    midpoint's travel pans; lifting one finger keeps panning with the other.
 *    The gesture STEALS both pointers (setPointerCapture on the canvas), so a
 *    child interaction that had one of them (element drag, pen stroke) gets
 *    `lostpointercapture` as its cancel signal, and the store's `pinching`
 *    flag stays up until the gesture fully ends so window-level drag
 *    listeners (grid resize/reorder) abort instead of committing.
 */
import { screenToBoard, type Point } from "@kola/shared";
import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from "react";
import { useStore } from "zustand";

import { safeSetPointerCapture, type CanvasStore } from "../../core/canvas.js";
import { trackViewport } from "../../core/viewport-tracker.js";
import type { BoardSession } from "../../core/session.js";
import type { BoardRect, CellsToolbarSelection } from "../../registries/toolbar-actions/index.js";
import { showToast } from "../../ui/toast.js";
import { ArrangeLayer } from "../arrange/ArrangeLayer.js";
import { CommentOverlays } from "../comments/CommentOverlays.js";
import { CommentPinsLayer } from "../comments/CommentPinsLayer.js";
import { DesignBandLayer } from "../design/DesignBandLayer.js";
import type { DesignUiStore } from "../design/store.js";
import type { CommentsUiStore } from "../comments/store.js";
import { DrawingLayer } from "../drawing/DrawingLayer.js";
import type { DrawUiStore } from "../drawing/store.js";
import { ConnectorsLayer } from "../connectors/ConnectorsLayer.js";
import { createConnectorUiStore } from "../connectors/store.js";
import {
  buildElementClipboard,
  clipboardOrigin,
  parseElementClipboard,
  pasteIntoCellPlacement,
  pastePlacement,
  serializeElementClipboard,
  type PasteCellTarget,
} from "../elements/clipboard.js";
import { resolveElementRect } from "../elements/contained.js";
import { useShareDeepLink } from "./useShareDeepLink.js";
import { ElementsLayer } from "../elements/ElementsLayer.js";
import { ImageDropLayer } from "../elements/ImageDropLayer.js";
import {
  idsInCellMarquee,
  idsInMarquee,
  isMarquee,
  marqueeRect,
  pruneSelected,
  toggleSelected,
  unionSelected,
} from "../elements/marquee.js";
import { createElementsUiStore, type ElementsUiStore } from "../elements/store.js";
import { ImportScriptLayer } from "../import-export/ImportScriptLayer.js";
import type { ImportUiStore } from "../import-export/store.js";
import { BrainstormGridLayer } from "../script/grid-brainstorm.js";
import { BgmPanel } from "../bgm/BgmPanel.js";
import { BgmSlotLayer } from "../bgm/BgmSlotLayer.js";
import { BgmStemDialog } from "../bgm/BgmStemDialog.js";
import { SectionsLayer } from "../sections/SectionsLayer.js";
import { SectionsOverlayLayer } from "../sections/SectionsOverlayLayer.js";
import { createSelectionUiStore, SelectionToolbar } from "../selection-toolbar/SelectionToolbar.js";
import { TranslateLayer } from "../translate/TranslateLayer.js";
import {
  GridTable,
  type CellMarquee,
  type GridApi,
  type GridLayoutSnapshot,
  type SelectedCell,
} from "./GridTable.js";
import { ToolRail } from "./ToolRail.js";

/** Dot-grid spacing in board units. */
const DOT_SPACING_BOARD = 24;

/** Stable empty selection — a fresh `[]` per clear would re-render every layer. */
const NO_SELECTION: readonly string[] = [];

/** A surface that owns its own keyboard/clipboard (never the board's). */
function isTypingTarget(node: EventTarget | null): boolean {
  const el = node as HTMLElement | null;
  return (
    el !== null &&
    (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable === true)
  );
}

export interface BoardCanvasProps {
  session: BoardSession;
  canvas: CanvasStore;
  /** Comments UI state (F-50..F-55) — owned by BoardScreen (top bar + panel
   * read it too); the canvas hosts the pins layer and the overlays. */
  commentsUi: CommentsUiStore;
  designUi: DesignUiStore;
  /** F-90 import state — owned by BoardScreen (its top bar has the picker);
   * the canvas hosts the drop target and the dialog. */
  importUi: ImportUiStore;
  /** Draw-tool state — owned by BoardScreen (its top bar has the toggle);
   * the canvas hosts the capture surface and the pen controls. */
  drawUi: DrawUiStore;
}

/** How long the F-123 attention ring stays on a linked item. */
const FOCUS_FLASH_MS = 1800;

export function BoardCanvas({
  session,
  canvas,
  commentsUi,
  importUi,
  drawUi,
  designUi,
}: BoardCanvasProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const gridApiRef = useRef<GridApi | null>(null);
  const sheetId = useStore(session.store, (s) => s.sheetId);
  const synced = useStore(session.store, (s) => s.synced);
  // NOTE: zoom only — never camera x/y (pan must not re-render this shell).
  const zoom = useStore(canvas, (s) => s.camera.zoom);
  // F-50 comment mode hides the F-27 toolbar (its capture layer would cover
  // the pill anyway — an unclickable toolbar must not stay visible). Rare
  // toggle, so subscribing here (unlike pan) cannot thrash the shell.
  const commentMode = useStore(commentsUi, (s) => s.mode);
  // Draw mode's capture surface covers the canvas the same way — the F-27
  // toolbar hides for the same reason.
  const drawMode = useStore(drawUi, (s) => s.mode);
  // F-34: the element selection is a SET — one, many (marquee / Cmd+click), or
  // none. Held here rather than in the elements layer because three surfaces
  // arbitrate over it: the grid (its own selection is mutually exclusive), the
  // F-27 toolbar, and the Del/copy/paste keyboard handling below.
  const [selectedElementIds, setSelectedElementIds] = useState<readonly string[]>(NO_SELECTION);
  // Live drag rects, shared by the elements layer (group drag) and the
  // connector layer (lines ride the motion) — see features/elements/store.
  const elementsUi = useMemo(() => createElementsUiStore(), []);
  // F-38 connector drafting, port arming, and the selected line.
  const connectorUi = useMemo(() => createConnectorUiStore(), []);
  const selectedConnectorId = useStore(connectorUi, (s) => s.selectedId);
  /**
   * The in-flight marquee's anchor. The BOX itself lives in `elementsUi` — it
   * changes every pointer frame, and holding it here would re-render the whole
   * shell (grid included) on each one, which is exactly what this file's pan
   * path is built to avoid.
   */
  const marqueeRef = useRef<{
    pointerId: number;
    from: Point;
    /** Cmd/Shift+drag EXTENDS the selection instead of replacing it. */
    additive: boolean;
    base: readonly string[];
  } | null>(null);
  // F-36: the grid's latest layout pass, held here so BOTH the elements layer
  // (contained-element placement) and the selection toolbar (a contained
  // element's on-screen rect) resolve cell rects from the same source the
  // comment pins do. Layout passes fire on structure/resize/auto-grow — never
  // on pan — so holding it in shell state adds no pan-time re-render.
  const [gridLayout, setGridLayout] = useState<GridLayoutSnapshot | null>(null);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const panDragRef = useRef<{ pointerId: number; lastX: number; lastY: number } | null>(null);
  // F-27 toolbar plumbing: grid/elements layers publish interaction state
  // into this store so only the toolbar re-renders (never this shell).
  const selectionUi = useMemo(() => createSelectionUiStore(), []);
  const onToolbarAnchor = useCallback(
    (anchor: CellsToolbarSelection | null) => selectionUi.getState().setCellAnchor(anchor),
    [selectionUi],
  );
  const onElementDragActive = useCallback(
    (active: boolean) => selectionUi.getState().setElementDragging(active),
    [selectionUi],
  );
  // F-52/F-36: the grid publishes each layout pass — for comment-pin anchoring
  // (comments store) and for contained-element placement (shell state).
  const onGridLayout = useCallback(
    (layout: GridLayoutSnapshot | null) => {
      setGridLayout(layout);
      commentsUi.getState().setGridLayout(layout);
    },
    [commentsUi],
  );

  /* ---------------- viewport tracking -------------------------------- */

  // The size reaches the store at mount and one frame after each resize —
  // never from inside the ResizeObserver callback, where a store write would
  // flush queued React updates mid-loop (core/viewport-tracker.ts says why).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    return trackViewport(el, canvas);
  }, [canvas]);

  /* ---------------- wheel: zoom (capture) + pan (bubble) ------------- */

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Capture phase, non-passive: ctrl/cmd+wheel (incl. trackpad pinch) zooms
    // the BOARD toward the pointer and must never trigger browser page zoom.
    const onWheelCapture = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      event.stopPropagation();
      const rect = el.getBoundingClientRect();
      const state = canvas.getState();
      const factor = Math.exp(-event.deltaY * 0.002);
      state.zoomAt(
        { x: event.clientX - rect.left, y: event.clientY - rect.top },
        state.camera.zoom * factor,
      );
    };
    // Plain wheel / two-finger scroll pans the board — everywhere, including
    // over the table (F-10: the table NEVER scrolls internally). EXCEPT over
    // a comment bubble (F-51): the thread popover owns its own scroll (the
    // message list is overflow-y: auto), and preventDefault+pan here would
    // both cancel that native scroll AND slide the board-anchored bubble out
    // from under the cursor mid-read.
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) return; // handled above
      const target = event.target as HTMLElement | null;
      // Same reasoning for a modal (the F-12 rename dialog, the F-70 translate
      // dialog) and for the brainstorm dialog, whose backdrop has its own class:
      // each is a DOM descendant of the canvas, so without this a wheel over
      // the dialog pans the board behind it instead of scrolling the thread
      // under the cursor (the brainstorm one was missing here until 2026-09-03).
      if (target?.closest(".comment-bubble, .modal-backdrop, .brainstorm-modal-backdrop") != null) {
        return;
      }
      event.preventDefault();
      canvas.getState().panBy(-event.deltaX, -event.deltaY);
    };
    el.addEventListener("wheel", onWheelCapture, { capture: true, passive: false });
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheelCapture, { capture: true });
      el.removeEventListener("wheel", onWheel);
    };
  }, [canvas]);

  /* ---------------- undo/redo shortcuts (F-43) ----------------------- */

  // Registered on the canvas shell, NOT inside the grid: the UndoManager is
  // session-lifetime (session.ts constructor note) precisely so pre-sync
  // floating-element edits are undoable — a listener mounted only once
  // sheetId resolves would leave Ctrl+Z dead while the grid still shows
  // "Loading board…" even though the elements layer is already interactive.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
      ) {
        return; // let the overlay editor keep its native undo
      }
      const key = event.key.toLowerCase();
      if (key === "z") {
        event.preventDefault();
        if (event.shiftKey) session.redo();
        else session.undo();
      } else if (key === "y") {
        event.preventDefault();
        session.redo();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [session]);

  /* ---------------- pan: space+drag / middle-drag / bg-drag ---------- */

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== "Space" || event.repeat) return;
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
      ) {
        return; // typing a space in an editor is not a pan
      }
      event.preventDefault();
      setSpaceHeld(true);
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === "Space") setSpaceHeld(false);
    };
    const onBlur = () => setSpaceHeld(false);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  // Middle-button drag pans from anywhere — captured before the grid sees it.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 1) return;
      event.preventDefault();
      event.stopPropagation();
      safeSetPointerCapture(el, event.pointerId);
      selectionUi.getState().setPanning(true); // F-27: hide the pill mid-pan
      const drag = { pointerId: event.pointerId, lastX: event.clientX, lastY: event.clientY };
      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId !== drag.pointerId) return;
        canvas.getState().panBy(ev.clientX - drag.lastX, ev.clientY - drag.lastY);
        drag.lastX = ev.clientX;
        drag.lastY = ev.clientY;
      };
      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== drag.pointerId) return;
        selectionUi.getState().setPanning(false);
        el.removeEventListener("pointermove", onMove);
        el.removeEventListener("pointerup", onUp);
        el.removeEventListener("pointercancel", onUp);
      };
      el.addEventListener("pointermove", onMove);
      el.addEventListener("pointerup", onUp);
      el.addEventListener("pointercancel", onUp);
    };
    el.addEventListener("pointerdown", onPointerDown, true);
    return () => el.removeEventListener("pointerdown", onPointerDown, true);
  }, [canvas, selectionUi]);

  /* ---------------- touch: two-finger pinch zoom + pan --------------- */

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    /** Live touch points on the canvas (client coords), in landing order. */
    const touches = new Map<number, { x: number; y: number }>();
    let pinch: { ids: [number, number]; dist: number; mid: { x: number; y: number } } | null = null;
    /** One finger left of a pinch — it keeps panning until it lifts too. */
    let residual: { pointerId: number; lastX: number; lastY: number } | null = null;

    const gestureActive = () => pinch !== null || residual !== null;

    const beginPinch = (idA: number, idB: number) => {
      const a = touches.get(idA);
      const b = touches.get(idB);
      if (a === undefined || b === undefined) return;
      pinch = {
        ids: [idA, idB],
        dist: Math.hypot(b.x - a.x, b.y - a.y),
        mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
      };
      residual = null;
      // A single-finger background pan in progress hands over to the gesture.
      panDragRef.current = null;
      canvas.getState().setPinching(true);
      selectionUi.getState().setPanning(true); // F-27: hide the pill mid-gesture
      // Steal both pointers: the previous capture target (an element mid-drag,
      // the pen surface mid-stroke) gets `lostpointercapture` — its cancel.
      safeSetPointerCapture(el, idA);
      safeSetPointerCapture(el, idB);
    };

    const endGesture = () => {
      // Deferred a macrotask so the final pointerup finishes propagating
      // first: stale window-listener drags (grid resize/pill reorder) read
      // `pinching` in their up handlers and must still see it raised.
      window.setTimeout(() => {
        if (!gestureActive()) {
          canvas.getState().setPinching(false);
          selectionUi.getState().setPanning(false);
        }
      }, 0);
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType !== "touch") return;
      // Same exclusion as wheel-pan: a thread bubble / modal owns its touches.
      const target = event.target as HTMLElement | null;
      if (target?.closest(".comment-bubble, .modal-backdrop, .brainstorm-modal-backdrop") != null) {
        return;
      }
      touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (residual !== null) {
        // Second finger returned mid-residual-pan: resume the pinch.
        const anchorId = residual.pointerId;
        event.preventDefault();
        event.stopPropagation();
        beginPinch(anchorId, event.pointerId);
      } else if (pinch === null && touches.size === 2) {
        const [first, second] = [...touches.keys()];
        if (first !== undefined && second !== undefined) {
          event.preventDefault();
          event.stopPropagation();
          beginPinch(first, second);
        }
      } else if (pinch !== null) {
        // 3rd+ finger while pinching: swallowed, never a child interaction.
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerType !== "touch") return;
      const point = touches.get(event.pointerId);
      if (point === undefined) return;
      point.x = event.clientX;
      point.y = event.clientY;
      if (pinch !== null && pinch.ids.includes(event.pointerId)) {
        event.preventDefault();
        event.stopPropagation();
        const a = touches.get(pinch.ids[0]);
        const b = touches.get(pinch.ids[1]);
        if (a === undefined || b === undefined) return;
        const dist = Math.hypot(b.x - a.x, b.y - a.y);
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const state = canvas.getState();
        // Midpoint travel pans; then zoom about the new midpoint keeps the
        // board point between the fingers fixed under them.
        state.panBy(mid.x - pinch.mid.x, mid.y - pinch.mid.y);
        if (pinch.dist > 0 && dist > 0) {
          const rect = el.getBoundingClientRect();
          state.zoomAt(
            { x: mid.x - rect.left, y: mid.y - rect.top },
            state.camera.zoom * (dist / pinch.dist),
          );
        }
        pinch.dist = dist;
        pinch.mid = mid;
      } else if (residual !== null && residual.pointerId === event.pointerId) {
        event.preventDefault();
        event.stopPropagation();
        canvas.getState().panBy(event.clientX - residual.lastX, event.clientY - residual.lastY);
        residual.lastX = event.clientX;
        residual.lastY = event.clientY;
      }
    };

    const onPointerEnd = (event: PointerEvent) => {
      if (event.pointerType !== "touch") return;
      if (!touches.delete(event.pointerId)) return;
      if (pinch !== null && pinch.ids.includes(event.pointerId)) {
        const otherId = pinch.ids[0] === event.pointerId ? pinch.ids[1] : pinch.ids[0];
        pinch = null;
        const other = touches.get(otherId);
        if (other !== undefined) {
          residual = { pointerId: otherId, lastX: other.x, lastY: other.y };
        } else {
          endGesture();
        }
      } else if (residual !== null && residual.pointerId === event.pointerId) {
        residual = null;
        endGesture();
      }
    };

    el.addEventListener("pointerdown", onPointerDown, true);
    el.addEventListener("pointermove", onPointerMove, true);
    el.addEventListener("pointerup", onPointerEnd, true);
    el.addEventListener("pointercancel", onPointerEnd, true);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown, true);
      el.removeEventListener("pointermove", onPointerMove, true);
      el.removeEventListener("pointerup", onPointerEnd, true);
      el.removeEventListener("pointercancel", onPointerEnd, true);
      canvas.getState().setPinching(false);
    };
  }, [canvas, selectionUi]);

  const beginPan = useCallback(
    (event: React.PointerEvent) => {
      panDragRef.current = {
        pointerId: event.pointerId,
        lastX: event.clientX,
        lastY: event.clientY,
      };
      safeSetPointerCapture(event.currentTarget as HTMLElement, event.pointerId);
      selectionUi.getState().setPanning(true); // F-27: hide the pill mid-pan
    },
    [selectionUi],
  );

  const movePan = useCallback(
    (event: React.PointerEvent) => {
      const drag = panDragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      canvas.getState().panBy(event.clientX - drag.lastX, event.clientY - drag.lastY);
      drag.lastX = event.clientX;
      drag.lastY = event.clientY;
    },
    [canvas],
  );

  const endPan = useCallback(() => {
    if (panDragRef.current !== null) selectionUi.getState().setPanning(false);
    panDragRef.current = null;
  }, [selectionUi]);

  /** Client pointer position → board units (the marquee lives in board space). */
  const boardPointFrom = useCallback(
    (event: { clientX: number; clientY: number }): Point => {
      const bounds = containerRef.current?.getBoundingClientRect();
      return screenToBoard(canvas.getState().camera, {
        x: event.clientX - (bounds?.left ?? 0),
        y: event.clientY - (bounds?.top ?? 0),
      });
    },
    [canvas],
  );

  /**
   * Left-drag on the empty background draws the F-34 MARQUEE — anything on the
   * grid or an element never reaches here as a background target. Panning keeps
   * every other route it has always had (space+drag, middle-drag, wheel /
   * trackpad, two-finger touch), which is the same split Miro and Figma use:
   * on a canvas, the empty background is where you select from.
   *
   * TOUCH is the exception and still pans with one finger: the mobile control
   * scheme (canvas.ts) gives one finger the camera and two fingers the zoom,
   * and there is no modifier key to select a region with instead.
   */
  const onBackgroundPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (event.button !== 0) return;
      const target = event.target as HTMLElement;
      if (target.dataset["canvasBg"] === undefined) return;
      const additive = event.metaKey || event.ctrlKey || event.shiftKey;
      if (!additive) {
        setSelectedElementIds(NO_SELECTION);
        connectorUi.getState().setSelected(null);
        // Clicking empty canvas deselects the grid too — one selection model
        // for both surfaces, same as selectElement's cross-clear.
        gridApiRef.current?.clearSelection();
      }
      if (spaceHeld || event.pointerType === "touch") {
        beginPan(event);
        return;
      }
      const from = boardPointFrom(event);
      marqueeRef.current = {
        pointerId: event.pointerId,
        from,
        additive,
        base: additive ? selectedElementIds : NO_SELECTION,
      };
      elementsUi.getState().setMarquee({ from, to: from });
      safeSetPointerCapture(event.currentTarget as HTMLElement, event.pointerId);
      // F-27: the pill hides for a marquee for the same reason it hides for a
      // pan — the gesture is about to change what is selected.
      selectionUi.getState().setPanning(true);
    },
    [beginPan, boardPointFrom, connectorUi, elementsUi, selectedElementIds, selectionUi, spaceHeld],
  );

  /** Drop the marquee with no selection change (a stolen pointer, Escape). */
  const cancelMarquee = useCallback(() => {
    if (marqueeRef.current === null) return;
    marqueeRef.current = null;
    elementsUi.getState().setMarquee(null);
    selectionUi.getState().setPanning(false);
  }, [elementsUi, selectionUi]);

  const onCanvasPointerMove = useCallback(
    (event: React.PointerEvent) => {
      const drag = marqueeRef.current;
      if (drag !== null && drag.pointerId === event.pointerId) {
        // Two fingers ALWAYS mean the camera (canvas.ts): the gesture steals
        // the pointer, so abandon the box rather than finish it on a stale one.
        if (canvas.getState().pinching) {
          cancelMarquee();
          return;
        }
        elementsUi.getState().setMarquee({ from: drag.from, to: boardPointFrom(event) });
        return;
      }
      movePan(event);
    },
    [boardPointFrom, cancelMarquee, canvas, elementsUi, movePan],
  );

  const onCanvasPointerUp = useCallback(
    (event: React.PointerEvent) => {
      const drag = marqueeRef.current;
      if (drag === null || drag.pointerId !== event.pointerId) {
        endPan();
        return;
      }
      marqueeRef.current = null;
      elementsUi.getState().setMarquee(null);
      selectionUi.getState().setPanning(false);
      const to = boardPointFrom(event);
      // Under the threshold this was a click on empty canvas, which already
      // cleared the selection on pointerdown.
      if (!isMarquee(drag.from, to)) return;
      const box = marqueeRect(drag.from, to);
      // Hit-tested on RESOLVED rects, so a contained element is judged by where
      // its cell actually is — the same rects the user is looking at.
      const hits = idsInMarquee(
        session.board.listElements().map((element) => ({
          id: element.id,
          rect: resolveElementRect(element, gridLayout),
        })),
        box,
      );
      const next = drag.additive ? unionSelected(drag.base, hits) : hits;
      setSelectedElementIds(next.length === 0 ? NO_SELECTION : next);
      if (next.length > 0) gridApiRef.current?.clearSelection();
    },
    [boardPointFrom, elementsUi, endPan, gridLayout, selectionUi, session],
  );

  /**
   * F-34 in-cell item box. The GRID decides the gesture (only it has the cell
   * geometry — see GridTable's `CellMarquee`); the shell owns the selection and
   * paints the box through the very same overlay/store as the background
   * marquee, so the two are one affordance to the user.
   */
  const onCellMarquee = useCallback(
    (marquee: CellMarquee | null) => {
      elementsUi
        .getState()
        .setMarquee(marquee === null ? null : { from: marquee.from, to: marquee.to });
    },
    [elementsUi],
  );

  const onCellMarqueeCommit = useCallback(
    (marquee: CellMarquee) => {
      elementsUi.getState().setMarquee(null);
      // Resolved rects, like the background marquee — a contained element is
      // judged by where its cell actually is, i.e. by what the user can see.
      const hits = idsInCellMarquee(
        session.board.listElements().map((element) => ({
          id: element.id,
          anchor: element.anchor,
          rect: resolveElementRect(element, gridLayout),
        })),
        marquee,
        marqueeRect(marquee.from, marquee.to),
      );
      setSelectedElementIds(hits.length === 0 ? NO_SELECTION : hits);
      // A box that caught something owns the selection; one that caught
      // nothing leaves the cell selected, exactly as a plain click would.
      if (hits.length > 0) gridApiRef.current?.clearSelection();
    },
    [elementsUi, gridLayout, session],
  );

  /* ---------------- element selection plumbing ----------------------- */

  const selectElement = useCallback(
    (elementId: string | null, additive: boolean) => {
      // One selection at a time across the whole canvas: an element, a line, or
      // a cell range — so Del and copy can never ambiguously hit two surfaces.
      connectorUi.getState().setSelected(null);
      if (elementId === null) {
        setSelectedElementIds(NO_SELECTION);
        return;
      }
      setSelectedElementIds((prev) => {
        if (additive) return toggleSelected(prev, elementId);
        return prev.length === 1 && prev[0] === elementId ? prev : [elementId];
      });
      gridApiRef.current?.clearSelection();
    },
    [connectorUi],
  );

  const selectConnector = useCallback(
    (connectorId: string | null) => {
      connectorUi.getState().setSelected(connectorId);
      if (connectorId === null) return;
      setSelectedElementIds(NO_SELECTION);
      gridApiRef.current?.clearSelection();
    },
    [connectorUi],
  );

  useEffect(() => {
    if (selectedElementIds.length === 0 && selectedConnectorId === null) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        cancelMarquee();
        return;
      }
      if (event.key !== "Delete" && event.key !== "Backspace") return;
      if (isTypingTarget(event.target)) return;
      // Capture + stop: the grid's own delete handling must not also fire.
      event.preventDefault();
      event.stopPropagation();
      if (selectedConnectorId !== null) {
        session.deleteConnector(selectedConnectorId);
        connectorUi.getState().setSelected(null);
        return;
      }
      // One transaction for the whole selection = one undo step (F-43), and
      // each element takes its F-38 connectors with it.
      session.deleteElements(selectedElementIds);
      setSelectedElementIds(NO_SELECTION);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [cancelMarquee, connectorUi, selectedConnectorId, selectedElementIds, session]);

  // Drop ids that disappeared (deleted remotely, or undone). Cheap on every
  // element edit: `pruneSelected` returns the SAME array when nothing changed,
  // so this re-renders only when the selection really shrank.
  const elementsVersion = useStore(session.store, (s) => s.elementsVersion);
  useEffect(() => {
    setSelectedElementIds((prev) => pruneSelected(prev, (id) => session.board.hasElement(id)));
  }, [session, elementsVersion]);

  const connectorsVersion = useStore(session.store, (s) => s.connectorsVersion);
  useEffect(() => {
    const selected = connectorUi.getState().selectedId;
    if (selected !== null && !session.board.hasConnector(selected)) {
      connectorUi.getState().setSelected(null);
    }
  }, [session, connectorUi, connectorsVersion]);

  /* ---------------- copy / paste of elements (F-34) ------------------- */

  // Both listeners run in the CAPTURE phase and stop propagation once they
  // claim the event, so the grid's TSV copy/paste (document/window, bubble
  // phase) and the F-30/F-31 image & link paste never also fire for it. When
  // there is nothing to claim they return untouched and every other path
  // behaves exactly as before.
  useEffect(() => {
    const onCopy = (event: ClipboardEvent) => {
      if (selectedElementIds.length === 0 || event.clipboardData === null) return;
      if (isTypingTarget(document.activeElement)) return;
      // A live DOM text selection means the user is copying THAT.
      if ((window.getSelection()?.toString() ?? "") !== "") return;
      const elements = selectedElementIds
        .map((id) => session.board.getElement(id))
        .filter((element): element is NonNullable<typeof element> => element !== undefined);
      if (elements.length === 0) return;
      const payload = buildElementClipboard(
        session.boardId,
        elements,
        session.board.listConnectors(),
        (element) => resolveElementRect(element, gridLayout),
      );
      event.clipboardData.setData("text/plain", serializeElementClipboard(payload));
      event.preventDefault();
      event.stopPropagation();
    };
    document.addEventListener("copy", onCopy, true);
    return () => document.removeEventListener("copy", onCopy, true);
  }, [gridLayout, selectedElementIds, session]);

  /**
   * The paste run: repeated Ctrl/Cmd+V of ONE payload cascade instead of
   * stacking exactly, and the run remembers the CELL it is pasting into.
   *
   * Remembering the target is what keeps a repeat where the user put the first
   * copy: the paste selects what it created and drops the cell selection (one
   * selection at a time), so by the second V there is no highlighted cell left
   * to read — without this the copy would jump back to the original's cell.
   * Highlighting a different cell re-targets the run and restarts its cascade.
   */
  const pasteRunRef = useRef<{ text: string; target: SelectedCell | null; step: number }>({
    text: "",
    target: null,
    step: 0,
  });
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      if (isTypingTarget(event.target)) return;
      const text = event.clipboardData?.getData("text/plain") ?? "";
      const clipboard = parseElementClipboard(text);
      if (clipboard === null) return; // not ours — the other paste paths own it
      event.preventDefault();
      event.stopPropagation();
      if (clipboard.boardId !== session.boardId) {
        // Payloads reference board-scoped assets, rows and columns (see
        // features/elements/clipboard) — a cross-board paste would land broken.
        showToast("Those items were copied from another board");
        return;
      }
      const sheetId = session.store.getState().sheetId;
      const cellExists = (rowId: string, colId: string) =>
        sheetId !== null &&
        session.board.hasRow(sheetId, rowId) &&
        session.board.hasColumn(sheetId, colId);
      const run = pasteRunRef.current;
      // A NEW payload starts a new run, which forgets the cell the last one was
      // pasting into: copying something else and pasting with no cell
      // highlighted must land next to ITS own original, not wherever the
      // previous paste happened to go.
      if (run.text !== text) {
        run.text = text;
        run.target = null;
        run.step = 0;
      }
      // A highlighted cell is the paste target — the items go INTO it. A
      // DIFFERENT cell restarts the cascade so the copy lands at that cell's
      // corner rather than continuing the last one's stagger.
      const picked = gridApiRef.current?.selectedCell() ?? null;
      if (picked !== null) {
        const moved =
          run.target === null ||
          run.target.rowId !== picked.rowId ||
          run.target.colId !== picked.colId;
        if (moved) run.step = 0;
        run.target = picked;
      }
      run.step += 1;
      // A target whose row/column was deleted since the last paste is no target
      // at all — the same C4 rule `pastePlacement` applies to a copied anchor.
      const target: PasteCellTarget | null =
        run.target !== null && cellExists(run.target.rowId, run.target.colId) ? run.target : null;
      if (target === null) run.target = null;
      const origin = clipboardOrigin(clipboard.elements);
      const duplicates = clipboard.elements.map((element) => {
        const placement =
          target !== null
            ? pasteIntoCellPlacement(element, target, origin, run.step)
            : pastePlacement(element, run.step, cellExists);
        return {
          ref: element.ref,
          type: element.type,
          w: element.w,
          h: element.h,
          rotation: element.rotation,
          x: placement.x,
          y: placement.y,
          anchor: placement.anchor,
          payload: element.payload,
        };
      });
      const created = session.duplicateElements(duplicates, clipboard.connectors);
      if (created.length === 0) return;
      // Select what was just pasted — the copies are what the next drag or
      // paste should act on, exactly like every other canvas tool.
      connectorUi.getState().setSelected(null);
      setSelectedElementIds(created);
      gridApiRef.current?.clearSelection();
    };
    window.addEventListener("paste", onPaste, true);
    return () => window.removeEventListener("paste", onPaste, true);
  }, [connectorUi, session]);

  const onCanvasPointerCancel = useCallback(() => {
    cancelMarquee();
    endPan();
  }, [cancelMarquee, endPan]);

  // The element actions on the F-27 pill are single-element (comment on it,
  // fill it) — a multi-selection has no one element for them to act on.
  const toolbarElementId = selectedElementIds.length === 1 ? (selectedElementIds[0] ?? null) : null;

  /* ---------------- F-123 share links: ?v= / ?cell= / ?el= ---------- */

  // The attention ring drawn around a linked item once it is framed — board
  // units, inside the camera layer, so it sits exactly on the item at any
  // zoom; keyed so a second link re-runs the animation.
  const [focusFlash, setFocusFlash] = useState<{ rect: BoardRect; key: number } | null>(null);
  const flashItem = useCallback((rect: BoardRect) => {
    setFocusFlash({ rect, key: Date.now() });
  }, []);
  useEffect(() => {
    if (focusFlash === null) return;
    const timer = window.setTimeout(() => setFocusFlash(null), FOCUS_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [focusFlash]);
  const selectCellById = useCallback(
    (rowId: string, colId: string) => gridApiRef.current?.selectCell(rowId, colId) ?? false,
    [],
  );
  const selectElementById = useCallback(
    (elementId: string) => selectElement(elementId, false),
    [selectElement],
  );
  useShareDeepLink(session, canvas, {
    gridLayout,
    selectCell: selectCellById,
    selectElement: selectElementById,
    flash: flashItem,
  });

  /* ------------------------------------------------------------------ */

  return (
    <div
      ref={containerRef}
      className={`board-canvas${spaceHeld ? " space-pan" : ""}`}
      data-canvas-bg="true"
      onPointerDown={onBackgroundPointerDown}
      onPointerMove={onCanvasPointerMove}
      onPointerUp={onCanvasPointerUp}
      onPointerCancel={onCanvasPointerCancel}
    >
      <CameraLayer canvas={canvas}>
        <DotLayer canvas={canvas} />
        {sheetId !== null ? (
          <GridTable
            session={session}
            sheetId={sheetId}
            // F-10 windowing reads the visible board rect from the camera
            // store directly (subscribed outside render), so a pan re-renders
            // the table only when the mounted row range actually moves — this
            // shell still never re-renders on pan.
            canvas={canvas}
            zoom={zoom}
            // F-16: a large paste is a bulk write like an import, and shares
            // its progress modal and its one-at-a-time guard.
            bulkUi={importUi}
            gridApi={gridApiRef}
            onGridPointerDown={() => {
              setSelectedElementIds(NO_SELECTION);
              connectorUi.getState().setSelected(null);
            }}
            onToolbarAnchor={onToolbarAnchor}
            onLayout={onGridLayout}
            onCellMarquee={onCellMarquee}
            onCellMarqueeCommit={onCellMarqueeCommit}
          />
        ) : (
          <div className="grid-loading">{synced ? "Preparing grid…" : "Loading board…"}</div>
        )}
        {/* Section-preview highlight bands (divide-sections) — right above the
            grid, under connectors/elements/pins; pointer-transparent, zero doc
            writes. Positioned from the same layout snapshot as the table. */}
        <SectionsOverlayLayer session={session} layout={gridLayout} />
        {/* F-126 stem slots: one chip per section, over the top cell of its run
            in the BGM column. In-camera like the bands above, but NOT
            pointer-transparent — a music slot exists to be clicked. */}
        <BgmSlotLayer session={session} layout={gridLayout} />
        {/* F-38 lines paint BEFORE the elements so they run underneath the
            items they join — and inside the same transform, so they can never
            misalign with them. */}
        <ConnectorsLayer
          session={session}
          canvas={canvas}
          containerRef={containerRef}
          layout={gridLayout}
          elementsUi={elementsUi}
          ui={connectorUi}
          onSelectConnector={selectConnector}
        />
        <ElementsLayer
          session={session}
          canvas={canvas}
          layout={gridLayout}
          selectedIds={selectedElementIds}
          onSelect={selectElement}
          elementsUi={elementsUi}
          connectorUi={connectorUi}
          onDragActive={onElementDragActive}
        />
        <MarqueeOverlay canvas={canvas} ui={elementsUi} />
        {/* Comment pins share the ONE board-space transform with the table
            and elements (docs/04 §5) — they can never misalign. */}
        <DesignBandLayer session={session} ui={designUi} canvas={canvas} layout={gridLayout} />
        <CommentPinsLayer session={session} canvas={canvas} ui={commentsUi} />
        {focusFlash !== null && (
          <div
            key={focusFlash.key}
            className="focus-flash"
            aria-hidden="true"
            style={{
              left: focusFlash.rect.x,
              top: focusFlash.rect.y,
              width: focusFlash.rect.w,
              height: focusFlash.rect.h,
              // Counter-scaled hairline, like every other overlay affordance.
              borderWidth: 2 / zoom,
              borderRadius: 6 / zoom,
            }}
          />
        )}
      </CameraLayer>
      <ImageDropLayer
        session={session}
        canvas={canvas}
        containerRef={containerRef}
        gridApi={gridApiRef}
      />
      {/* F-90: dropping a .txt/.csv/.tsv on the board imports it into the
          Original Script column. Every other file is ImageDropLayer's (an
          image or, F-135, a file element) — the two split the drop by file
          name and never both claim one. */}
      <ImportScriptLayer session={session} ui={importUi} containerRef={containerRef} />
      {/* F-27 selection context toolbar — OUTSIDE the camera transform, in
          screen coordinates (it subscribes to the camera itself). */}
      <SelectionToolbar
        session={session}
        canvas={canvas}
        ui={selectionUi}
        gridLayout={gridLayout}
        selectedElementId={toolbarElementId}
        spaceHeld={spaceHeld}
        commentMode={commentMode || drawMode}
        gridApi={gridApiRef}
      />
      {/* F-50/F-51 comment mode + composer/popover bubbles — screen space,
          same containing-block contract as the toolbar. */}
      <CommentOverlays session={session} canvas={canvas} ui={commentsUi} />
      {/* Drawing capture stays in the canvas; pen controls live in the header. */}
      <DrawingLayer
        session={session}
        canvas={canvas}
        draw={drawUi}
        containerRef={containerRef}
        layout={gridLayout}
      />
      {/* Miro-style left tool rail (Select/Comment/Draw) — the ONE place these
          modes are switched, by click or by keyboard (V/C/P). */}
      <ToolRail session={session} commentsUi={commentsUi} drawUi={drawUi} />
      {/* F-70 translate dialog (opened by the toolbar's translate icon) and
          the progress chip for runs in flight. The translated CELLS arrive on
          their own — the server writes them into the doc and sync paints them. */}
      <TranslateLayer session={session} />
      {/* F-60 arrange-script preview dialog (opened by the toolbar's arrange
          icon on the Original Script column). */}
      <ArrangeLayer session={session} />
      {/* Divide-into-sections panel (opened by the sections icon in the
          board's top-right pill, BoardScreen) — a docked card, so the
          highlight bands it drives stay visible while the user decides. */}
      <div className="board-canvas-panels">
        <SectionsLayer session={session} />
        {/* F-126/F-127 BGM track: details expanded from Music in the header
          and the add-music
          dialog the slot chips in the BGM column open. */}
        <BgmPanel session={session} />
        <BgmStemDialog session={session} />
      </div>
      {/* Brainstorm dialog (opened by the toolbar's Ask-AI sparkle on any
          cell selection). Registering the action is this import's side effect. */}
      <BrainstormGridLayer session={session} />
      {spaceHeld && (
        <div
          className="pan-overlay"
          onPointerDown={(event) => {
            event.preventDefault();
            beginPan(event);
          }}
          onPointerMove={movePan}
          onPointerUp={endPan}
          onPointerCancel={endPan}
        />
      )}
    </div>
  );
}

/**
 * F-34 marquee box — drawn INSIDE the camera transform in board units, so it
 * stays over the same board content if the user pans or zooms mid-drag (the
 * box is what the hit test uses, and the two must never disagree). The border
 * counter-scales so it reads as a hairline at every zoom.
 *
 * Subscribes to the box itself, so a selection drag re-renders THIS and nothing
 * else — the same containment the camera layer gives a pan.
 */
function MarqueeOverlay({ canvas, ui }: { canvas: CanvasStore; ui: ElementsUiStore }) {
  const marquee = useStore(ui, (s) => s.marquee);
  const zoom = useStore(canvas, (s) => s.camera.zoom);
  if (marquee === null) return null;
  const box = marqueeRect(marquee.from, marquee.to);
  return (
    <div
      className="marquee-box"
      style={{
        left: box.x,
        top: box.y,
        width: box.w,
        height: box.h,
        borderWidth: 1 / zoom,
      }}
    />
  );
}

/**
 * The ONE transformed container (F-121): translate + scale, so every child —
 * dot background, table, elements, future pins — shares the same transform
 * and can never misalign. Subscribes to the camera ITSELF so a pan/zoom
 * re-renders only this wrapper div; React bails out of the (referentially
 * stable) children.
 */
function CameraLayer({ canvas, children }: PropsWithChildren<{ canvas: CanvasStore }>) {
  const camera = useStore(canvas, (s) => s.camera);
  const tx = -camera.x * camera.zoom;
  const ty = -camera.y * camera.zoom;
  return (
    <div
      className="camera-layer"
      style={{ transform: `translate(${tx}px, ${ty}px) scale(${camera.zoom})` }}
    >
      {children}
    </div>
  );
}

/**
 * Dot-grid background INSIDE the transformed layer: a board-unit-sized cover
 * of the visible board rect, snapped to the dot spacing so the pattern never
 * jitters while panning. Dots scale with zoom like everything else.
 */
function DotLayer({ canvas }: { canvas: CanvasStore }) {
  const camera = useStore(canvas, (s) => s.camera);
  const viewport = useStore(canvas, (s) => s.viewport);
  const spacing = DOT_SPACING_BOARD;
  const left = Math.floor(camera.x / spacing - 1) * spacing;
  const top = Math.floor(camera.y / spacing - 1) * spacing;
  const width = viewport.width / camera.zoom + 3 * spacing;
  const height = viewport.height / camera.zoom + 3 * spacing;
  return (
    <div
      className="dot-layer"
      data-canvas-bg="true"
      style={{
        left,
        top,
        width,
        height,
        backgroundSize: `${spacing}px ${spacing}px`,
        opacity: camera.zoom < 0.5 ? 0.5 : 1,
      }}
    />
  );
}
